import { IconLayer } from '@deck.gl/layers';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GOOGLE } from '@rsos/constants/mapProviders';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import spriteMapping from '../../assets/calls/sprites-output/sprites-info.json';
import callsSprite from '../../assets/calls/sprites-output/sprites_2x.png';
import {
  hideAirAmbulancePopover,
  showAirAmbulancePopover,
} from '../MapPopover/mapPopoverSlice';
import layers from '../storeLayers';

const TEMP_LOCATION_LAYER_ID = 'temp-location-default';

const MARKER_SIZES = {
  sizeScale: 60, // this is the factor to scale the icon.
  sizeMinPixels: 38, // when zoomed out, the max height of the icon.
  sizeMaxPixels: 40, // when zoomed in, the max height of the icon.
};

const useTempLocationGL = ({ map, overlay, isMapIconDisabled }) => {
  const dispatch = useDispatch();
  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const airAmbulanceLocation = useSelector(
    state => state.airAmbulance.temporaryLocation,
  );

  const clickLocation = useSelector(state => {
    const clickInfo = state.clickToIdentify?.mapClickInfo;
    return clickInfo
      ? {
          latitude: clickInfo.latitude,
          longitude: clickInfo.longitude,
        }
      : null;
  });

  const temporaryLocation = useMemo(() => {
    // only 1 location can be shown at a time so its safe to use pipes here
    return airAmbulanceLocation || clickLocation;
  }, [airAmbulanceLocation, clickLocation]);

  const { selectedCallerID, callQueue } = useSelector(
    state => state.emergencyCalls,
  );

  const hasLocationToRender = useMemo(
    () => getMostRecentLoc(callQueue[selectedCallerID]),
    [selectedCallerID, callQueue],
  );

  const isSameLocation = useMemo(() => {
    if (hasLocationToRender && temporaryLocation) {
      const selectedLat = hasLocationToRender.latitude;
      const selectedLong = hasLocationToRender.longitude;
      if (
        selectedLat &&
        selectedLong &&
        selectedLat !== temporaryLocation.latitude &&
        selectedLong !== temporaryLocation.longitude
      ) {
        return false;
      }
      return true;
    }
    return false;
  }, [hasLocationToRender, temporaryLocation]);

  // Wrapped in useCallback so it can be a stable dependency of the useEffect
  // below without triggering it on every render.
  const createTempLocationLayer = useCallback(
    data =>
      new IconLayer({
        id: TEMP_LOCATION_LAYER_ID,
        data,
        getPosition: d => {
          return [parseFloat(d.longitude), parseFloat(d.latitude)];
        },
        pickable: !isMapIconDisabled,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 60],
        iconAtlas: callsSprite,
        iconMapping: spriteMapping,
        sizeUnits: 'meters',
        ...MARKER_SIZES,
        getIcon: () => 'historic_call_marker', // matches entry in iconMapping
        onHover: () => {
          // no hover state for temporary icon
        },
        onClick: () => {
          // do nothing
        },
        visible: true,
      }),
    [isMapIconDisabled],
  );

  useEffect(() => {
    if (!overlay) return;

    if (temporaryLocation) {
      const layer = createTempLocationLayer([temporaryLocation]);
      layers.save(layer);
      overlay.setLayers(layers.getAll());

      if (!isSameLocation && temporaryLocation) {
        // Only show air ambulance popover for air ambulance locations
        if (airAmbulanceLocation) {
          dispatch(showAirAmbulancePopover(temporaryLocation));
          panMapTo({
            mapProvider,
            mapInstance: map.mapInstance,
            latitude: parseFloat(temporaryLocation.latitude),
            longitude: parseFloat(temporaryLocation.longitude),
            zoom:
              mapProvider === GOOGLE
                ? map.mapInstance.zoom
                : map.mapInstance.view.zoom,
          });
        }
      }
    } else {
      dispatch(hideAirAmbulancePopover());
      const emptyLayer = createTempLocationLayer([]);
      layers.save(emptyLayer);
      overlay.setLayers(layers.getAll());
    }
  }, [
    overlay,
    temporaryLocation,
    airAmbulanceLocation,
    isSameLocation,
    mapProvider,
    map?.mapInstance,
    dispatch,
    createTempLocationLayer,
  ]);
};

const TempLocationGL = React.memo(({ map, overlay, isMapIconDisabled }) => {
  useTempLocationGL({ map, overlay, isMapIconDisabled });
  return <></>;
});

export default TempLocationGL;
